import React from 'react';
import { Button, FormikDatepicker, Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Field, FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { Loader } from 'app/shared/loader/loader';
import { constructionLegalityDocumentAddFormSchema } from 'app/edozvola/cases/case-construction-legality/model/case-construction-legality.schema';
import {
  fetchSavedCaseConstructionLegalityDocumentQuery,
  updateCaseConstructionLegalityDocumentQuery,
} from 'app/edozvola/cases/case-construction-legality/service/case-construction-legality.queries';
import { APP_DATE_FORMAT } from 'app/config/constants';
import error = toast.error;
import {
  caseConstructionLegalityDocumentAddFormInitial
} from 'app/edozvola/cases/case-construction-legality/model/case-construction-legality.model';
import {
  fetchCaseConstructionLegalityDocument
} from 'app/edozvola/cases/case-construction-legality/service/case-construction-legality.service';

export interface ICaseConstructionLegalityDocumentsAddFormProps {
  idConstructionLegalityDocumentMap: string;
  onCloseCallback: any;
}

const CaseConstructionLegalityDocumentsAddForm = (props: ICaseConstructionLegalityDocumentsAddFormProps) => {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = fetchSavedCaseConstructionLegalityDocumentQuery(id, props.idConstructionLegalityDocumentMap);

  const formik =
    useFormik({
      initialValues: data || caseConstructionLegalityDocumentAddFormInitial,
      initialTouched: {
        webId: true,
      },
      onSubmit: values => mutate(values),
      validationSchema: constructionLegalityDocumentAddFormSchema,
      enableReinitialize: true,
      validateOnMount: true,
    });

  const { mutate, isPending: isSaving } = updateCaseConstructionLegalityDocumentQuery(id, formik.resetForm, () => props.onCloseCallback());

  const fetchCaseConstructionLegalityDocumentData = () => {
    fetchCaseConstructionLegalityDocument({ idCase: id, webId: formik.values.webId })
      .then(data => {
        formik.setValues({ ...data, dataFetched: true });
      })
      .catch(err => {
        showToast(error, err);
      });
  };

  return (
    <Modal scrollable isOpen size={'xl'} centered>
      <ModalHeader toggle={props.idConstructionLegalityDocumentMap !== '' ? () => props.onCloseCallback() : null}>
        {props.idConstructionLegalityDocumentMap !== '' ? 'Pregled akta' : 'Unos akta'}
      </ModalHeader>
      <ModalBody>
        {isLoading && <Loader />}
        {!isLoading && (
          <FormikProvider value={formik}>
            <div className={'d-flex flex-column gap-3'}>
              <Row>
                <Col md={4}>
                  <Label labelText={'Šifra predmeta'} />
                  <div className={'d-flex gap-2'}>
                    <Input
                      id="webId"
                      name="webId"
                      disabled={props.idConstructionLegalityDocumentMap !== ''}
                      maxLength={255}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.webId}
                      error={formik.errors.webId && formik.touched.webId ? `${formik.errors.webId}` : ''}
                    />
                    <div>
                      <Button
                        disabled={!!formik.errors.webId || props.idConstructionLegalityDocumentMap !== ''}
                        label={'Dohvati'}
                        onClick={fetchCaseConstructionLegalityDocumentData}
                        size={'lg'}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
              <hr/>
              <Row>
                <Col md={9}>
                  <Label labelText={'Naziv akta'} />
                  <Input
                    id="finalActDocumentName"
                    name="finalActDocumentName"
                    maxLength={200}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.finalActDocumentName}
                    disabled={true}
                    error={formik.errors.finalActDocumentName}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Label labelText={'Klasifikacijska oznaka'} />
                  <Input
                    id="classification"
                    name="classification"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.classification}
                    disabled={true}
                    error={formik.errors.classification}
                  />
                </Col>
                <Col md={3}>
                  <Label labelText={'Urudžbeni broj'} />
                  <Input
                    id="finalActFileNumber"
                    name="finalActFileNumber"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.finalActFileNumber}
                    disabled={true}
                    error={formik.errors.finalActFileNumber}
                  />
                </Col>
                <Col md={3}>
                  <Label labelText={'Način rješavanja'} />
                  <Input
                    id="solutionType"
                    name="solutionType"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.solutionType}
                    disabled={true}
                    error={formik.errors.solutionType}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Label labelText={'Datum rješavanja'} />
                  <Field
                    id={`finalDocumentAdmissionDate`}
                    name={`finalDocumentAdmissionDate`}
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled error={formik.errors.finalDocumentAdmissionDate as string} />
                    )}
                  />
                </Col>
                <Col md={3}>
                  <Label labelText={'Datum izvršnosti'} />
                  <Field
                    id={`executionDate`}
                    name={`executionDate`}
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled error={formik.errors.executionDate as string} />
                    )}
                  />
                </Col>
                <Col md={3}>
                  <Label labelText={'Datum pravomoćnosti'} />
                  <Field
                    id={`effectivenessDate`}
                    name={`effectivenessDate`}
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled error={formik.errors.effectivenessDate as string} />
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Label labelText={'Upravna jedinica'} />
                  <Input
                    id="administrativeUnit"
                    name="administrativeUnit"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.administrativeUnit}
                    disabled={true}
                    error={formik.errors.administrativeUnit}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Label labelText={'Nadležno tijelo'} />
                  <Input
                    id="authority"
                    name="authority"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.authority}
                    disabled={true}
                    error={formik.errors.authority}
                  />
                </Col>
                <Col md={6}>
                  <Label labelText={'Nadležni ured (stari naziv)'} />
                  <Input
                    id="authorityOfficeOldName"
                    name="authorityOfficeOldName"
                    maxLength={100}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!!props.idConstructionLegalityDocumentMap}
                    value={formik.values.authorityOfficeOldName}
                  />
                </Col>
              </Row>
            </div>
          </FormikProvider>
        )}
      </ModalBody>
      <ModalFooter>
        {props.idConstructionLegalityDocumentMap === '' && (
          <>
            <Button size={'lg'} label={'Odustani'} outline onClick={() => props.onCloseCallback()} />
            <Button size={'lg'} label={'Spremi'} isLoading={isSaving} color={'info'} onClick={() => formik.handleSubmit()} disabled={!formik.values.dataFetched || !formik.isValid} />
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CaseConstructionLegalityDocumentsAddForm;
